// D7-08 (FE-6.7/6.8): agent type/status normalization at the API boundary.
//
// The server's agent registry and health endpoints historically returned a mix
// of cases ('PERSONAL' and 'personal', 'ONLINE' and 'online', …), which forced
// `String(agent.type ?? 'SHARED').toUpperCase()`-style defensive code at every
// consumer — and let subtle bugs through (e.g. `agent.type !== 'user'` silently
// passing for 'USER'). These helpers collapse any raw value onto the canonical
// lowercase unions exactly once, where agents enter the client (normalizeAgent
// in chatStore). Downstream code may rely on lowercase values.
import type { AgentStatus, AgentType } from '../types';

export const AGENT_TYPES: readonly AgentType[] = ['personal', 'shared', 'public', 'user', 'chatbot'];

export const AGENT_STATUSES: readonly AgentStatus[] = [
  'online',
  'slow',
  'offline',
  'maintenance',
  'error',
  'deploying',
  'healthy',
  'degraded',
  'unhealthy',
];

/** Collapse a raw agent type onto the canonical lowercase union. Unknown or empty values map to the fallback (default 'shared'). */
export function normalizeAgentType(value: unknown, fallback: AgentType = 'shared'): AgentType {
  const normalized = String(value ?? '').trim().toLowerCase();
  return (AGENT_TYPES as readonly string[]).includes(normalized) ? (normalized as AgentType) : fallback;
}

/** Collapse a raw agent status onto the canonical lowercase union. Unknown or empty values map to the fallback (default 'offline'). */
export function normalizeAgentStatus(value: unknown, fallback: AgentStatus = 'offline'): AgentStatus {
  const normalized = String(value ?? '').trim().toLowerCase();
  return (AGENT_STATUSES as readonly string[]).includes(normalized) ? (normalized as AgentStatus) : fallback;
}
